import { FinanceRow } from "@/types";

// Helper function to get contractor name
export const getContractorName = (item: any): string => {
  if (item.Contractor?.companyName) return item.Contractor.companyName;
  if (item.Contractor?.fullName) return item.Contractor.fullName;
  return item.contractor || "";
};

// Helper function to determine project type
export const getProjectType = (item: any): string => {
  if (item.sdcpProject?.CARE === "Y") return "CARE";
  if (item.sdcpProject?.FERA === "Y") return "FERA";
  return item.projectType || "Market Rate";
};

// Helper function to get applicant state
export const getApplicantState = (item: any): string => {
  return item.applicantState || item.sdcpProject?.State || item.state || "";
};

// Helper function to get CoC/Care/Fera status
export const getCoCCareFera = (
  item: any
): { coc: string; care: string; fera: string } => {
  const coc = item.sdcpProject?.isCoC === "Y" ? "Yes" : "No";
  const care = item.sdcpProject?.CARE === "Y" ? "Yes" : "No";
  const fera = item.sdcpProject?.FERA === "Y" ? "Yes" : "No";
  return { coc, care, fera };
};

// Helper function to get LDC Account Number
export const getLdcAccountNumber = (item: any): string => {
  return item.accountHash || item.sdcpProject?.LDC_Account_No || "";
};

// General project data mapping function - not finance-specific
export const mapApiToRow = (item: any): FinanceRow => {
  // Helper function to construct new solar system size
  const getNewSolarSystemSize = () => {
    const kwdc = item.newSolarKwdc || item.solarKwdc;
    return kwdc ? Number(kwdc) : 0;
  };

  // Format CoC/Care/Fera for FinanceRow (which expects a string)
  const { coc, care, fera } = getCoCCareFera(item);
  const cocCareFeraFormatted = `${coc}/${care}/${fera}`;

  return {
    id: item.id,
    customerName: item.customerName || "",
    accountEmail: item.accountEmail || "",
    address: item.address || "",
    city: item.city || "",
    applicantState: getApplicantState(item),
    zipCode: item.zipCode || "",
    contractor: getContractorName(item),
    payee: item.payee || "",
    payeeType: item.payeeType || "",
    ldcAccountNumber: getLdcAccountNumber(item),
    nemSbp: item.sdcpProject?.NEM_SBP || "",
    nemStartDate: item.sdcpProject?.NEM_Start_Date || "",
    sbpStartDate: item.sdcpProject?.SBP_Start_Date || "",
    utilityRate: item.sdcpProject?.Utility_Rate || "",
    utilityRateStartDate: item.sdcpProject?.Utility_Rate_Start_Date || "",
    newSolarSystemSize: getNewSolarSystemSize(),
    batteryOem: item.batteryOem || "",
    batteryModel: item.batteryModel || "",
    batterySerialNumber: item.batterySerialNumber || "",
    numberOfBatteries: item.numberOfBatteries || "",
    batteryNameplateKw: item.batteryNameplateKw || "",
    usableCapacity: item.usableCapacity || "",
    usableCapacityIncentive: item.usableCapacityIncentive || "",
    ptoNameplateKw: item.ptoNameplateKw || "",
    ptoSolarKwAc: item.ptoSolarKwAc || "",
    projectType: getProjectType(item),
    cocCareFera: cocCareFeraFormatted,
    upfrontIncentivePayment: item.upfrontIncentivePayment || "",
    total_cost: item.total_cost || "",
    termsAndConditions: item.termsAndConditions || "",
    ptoLetterDate: item.ptoLetterDate || "",
    ptoSubmittedDate: item.ptoSubmittedDate || "",
    finance_review_date: item.finance_review_date || "",
    note:
      item.finance_notes ||
      item.note ||
      (item.comment && item.comment.note) ||
      "",

    // Legacy fields for compatibility
    batteryProduct: item.batteryType || "",
    solarKwdc: item.solarKwdc || "",
    solarKwac: item.solarKwac || "",
    solarAnnualProduction: item.solarAnnualProduction || "",
    newSolarKwdc: item.newSolarKwdc || "",
    newSolarKwac: item.newSolarKwac || "",
    newSolarAnnualProduction: item.newSolarAnnualProduction || "",
    incentiveStatus: item.incentiveStatus || "",
    enrolmentStatus: item.enrolmentStatus || "",
    approvalDate: item.approvalDate || "",
    createdAt: item.createdAt || "",
    updatedAt: item.updatedAt || "",
    accountHash: item.accountHash || "",
    state: item.state || "",
  };
};
